import { useCallback, useEffect, useRef, useState } from 'react'

import { AddToBasketTimer } from '@/widgets/Ai-chat/ui/add-to-basket-timer'

import { useAiChat, useAiChatUi } from '@/entities/ai-chat'

import { ChatLoader } from './chat-loader/chat-loader'
import { CloseChatApplyModal } from './close-chat-apply-modal/close-chat-apply-modal'
import { HeaderPanel } from './header-panel/header-panel'
import { MessageEmptyWindow, type MessageEmptyWindowHandle } from './message-empty-window/message-empty-window'
import { MessegeInput } from './message-input/MessageInput'
import { MessageList, type MessageListHandle } from './message-list/MessageList'
import { OperatorSwitch } from './operator-switch'
import { PersonalAccountBanner } from './personal-account-banner'

import cl from './chat.module.scss'

type ChatProps = {
	isOpen: boolean
	onClose: () => void
	goToOperator?: (message: string) => void
	isDesktop?: boolean
}

export const Chat = ({ isOpen, onClose, goToOperator, isDesktop }: ChatProps) => {
	const [isApplyOpen, setApplyOpen] = useState(false)
	const { pendingMessage, clearPendingMessage } = useAiChatUi()

	const {
		title,
		messages,
		paging,
		startText,
		streamStatus,
		streamMessage,
		isInitialization,
		isActive,
		isMoreLoading,
		isStreaming,
		showOperatorButton,
		showPersonalAccountButton,
		productIdsToCart,
		sendMessage,
		cancelStream,
		createChat,
		loadHistory,
		loadNextPage,
		lastUserMessage,
		clearProductIdsToCart
	} = useAiChat()

	const messageListRef = useRef<MessageListHandle>(null)
	const emptyWindowRef = useRef<MessageEmptyWindowHandle>(null)
	const pendingMessageRef = useRef<string | null>(null)

	useEffect(() => {
		if (!isDesktop || !isOpen) return

		document.documentElement.style.overflow = 'hidden'

		return () => {
			document.documentElement.style.overflow = ''
		}
	}, [isOpen, isDesktop])

	useEffect(() => {
		pendingMessageRef.current = pendingMessage
	}, [pendingMessage])

	useEffect(() => {
		const init = async () => {
			if (!isOpen) return

			const messageToSend = pendingMessageRef.current
			if (messageToSend) {
				clearPendingMessage()
			}

			await loadHistory()

			if (messageToSend) {
				await sendMessage(messageToSend)
			}
		}

		void init()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen])

	const handleSend = async (inputValue: string) => {
		if (!inputValue.trim()) return

		await sendMessage(inputValue)
	}

	const handleInputFocus = useCallback(() => {
		messageListRef.current?.scrollToBottom()
		emptyWindowRef.current?.scrollToBottom()
	}, [])

	const closeHandler = () => {
		if (!isStreaming) {
			onClose()
		} else {
			setApplyOpen(true)
		}
	}

	return (
		<>
			<div className={cl['chatContainer']}>
				<HeaderPanel title={title} onClose={closeHandler} />

				<>
					{isInitialization && <ChatLoader />}
					{!isInitialization && !messages?.length && (
						<MessageEmptyWindow ref={emptyWindowRef} setStartText={handleSend} />
					)}
					{!isInitialization && !!messages?.length && (
						<MessageList
							ref={messageListRef}
							messages={messages}
							streamMessage={streamMessage}
							streamStatus={streamStatus}
							onLoad={loadNextPage}
							isMoreLoading={isMoreLoading}
							showMoreButton={paging.has_more}
						/>
					)}
				</>

				{!productIdsToCart.length && showOperatorButton && !!messages?.length && (
					<OperatorSwitch lastUserMessage={lastUserMessage} goToOperator={goToOperator} />
				)}
				{!productIdsToCart.length && showPersonalAccountButton && !!messages?.length && (
					<PersonalAccountBanner onNavigate={onClose} />
				)}
				<AddToBasketTimer productIdsToCart={productIdsToCart} onComplete={clearProductIdsToCart} />
				<MessegeInput
					isDesktop={isDesktop}
					onCreateChat={createChat}
					onSumbmit={handleSend}
					isDisabled={(isInitialization && isActive) || isStreaming}
					startText={startText}
					isActive={isActive}
					onFocus={handleInputFocus}
				/>
			</div>
			<CloseChatApplyModal
				isOpen={isApplyOpen}
				onClose={() => setApplyOpen(false)}
				onApply={() => {
					cancelStream()
					onClose()
				}}
			/>
		</>
	)
}

export default Chat
